import React, { useState, useEffect, useCallback } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { ChevronRightIcon } from '@heroicons/react/20/solid';
import { bookingApi } from '../../../utils/bookingApi';
import { formatDate } from '../../../utils/dates';
import AdminRemovedMembers from './AdminRemovedMembers';
import ApprovalsWaiting from '../../../components/sessions/ApprovalsWaiting';
import { ROLE_LABELS } from './memberShared';
import {
  PageHeader, FilterChips, Disclosure, Sheet, Skeleton, ErrorState, useToast,
} from '../../../components/ui';
import '../booking-shared.css';
import './AdminMembers.css';
import { useFeature, useFeatures } from '../../../hooks/useFeature';
import { useAuth } from '../../../contexts/AuthContext';
import { roleSummary } from '../../../components/members/StaffRoles';

// A new user is a member (no roles) or staff with the roles ticked
const EMPTY_CREATE = { firstName: '', lastName: '', email: '', phone: '', roleIds: [] };

const ROLE_CHIPS = [
  { id: '', label: 'All roles' }, { id: 'CLUB_ADMIN', label: 'Admin' }, { id: 'COACH', label: 'Coach' },
  { id: 'WELFARE', label: 'Welfare' }, { id: 'STAFF', label: 'Staff' }, { id: 'ADULT', label: 'Adult' },
  { id: 'GYMNAST', label: 'Gymnast' }, { id: 'ARCHIVED', label: 'Archived' },
];

const PAGE_SIZE = 25;

const ageFrom = (dob) => {
  if (!dob) return null;
  const d = new Date(dob);
  const now = new Date();
  let years = now.getFullYear() - d.getFullYear();
  if (now < new Date(now.getFullYear(), d.getMonth(), d.getDate())) years -= 1;
  return years;
};

// Adding someone: a member (no roles) or staff (roles ticked). Club admins only.
function AddMemberSheet({ onCreated, onClose }) {
  const featureOn = useFeatures();
  const [form, setForm] = useState(EMPTY_CREATE);
  const [creating, setCreating] = useState(false);
  const [error, setError] = useState(null);
  const [clubRoles, setClubRoles] = useState(null);
  const [rolesError, setRolesError] = useState(null);
  useEffect(() => {
    bookingApi.getRoles()
      .then(r => setClubRoles(r.data))
      .catch(err => setRolesError(err.response?.data?.error || "Couldn't load the roles."));
  }, []);
  const isStaffForm = form.roleIds.length > 0;
  const set = (key) => (e) => setForm(f => ({ ...f, [key]: e.target.value }));
  const toggleRole = (id) => setForm(f => ({
    ...f, roleIds: f.roleIds.includes(id) ? f.roleIds.filter(x => x !== id) : [...f.roleIds, id],
  }));
  const dirty = Object.keys(EMPTY_CREATE).some(k => (k === 'roleIds' ? form.roleIds.length > 0 : form[k] !== ''));

  const submit = async () => {
    setError(null);
    if (!form.firstName.trim() || !form.lastName.trim()) { setError('Enter their first and last name.'); return; }
    if (!form.email.trim()) { setError('Enter their email.'); return; }
    if (!isStaffForm && !form.phone.trim()) { setError('Enter a phone number. It is optional for staff only.'); return; }
    setCreating(true);
    try {
      const { roleIds, ...details } = form;
      const ordered = (clubRoles || []).filter(r => roleIds.includes(r.id)).map(r => r.id);
      const res = await bookingApi.createUser({ ...details, role: 'ADULT', ...(ordered.length ? { roleIds: ordered } : {}) });
      onCreated(res.data);
    } catch (err) {
      setError(err.response?.data?.error || "Couldn't add them.");
      setCreating(false);
    }
  };

  return (
    <Sheet
      title="Add a member"
      wide
      onClose={onClose}
      dirty={dirty}
      onSubmit={submit}
      footer={(requestClose) => (
        <div className="bk-actions">
          <button type="submit" disabled={creating} className="bk-btn bk-btn--primary">{creating ? 'Creating…' : 'Create user'}</button>
          <button type="button" className="bk-btn" onClick={requestClose}>Cancel</button>
        </div>
      )}
    >
      <div className="ml-create">
        <div className="bk-grid-2">
          <label className="bk-label">First name
            <input className="bk-input" value={form.firstName} onChange={set('firstName')} required autoComplete="off" />
          </label>
          <label className="bk-label">Last name
            <input className="bk-input" value={form.lastName} onChange={set('lastName')} required autoComplete="off" />
          </label>
        </div>
        <label className="bk-label">Email
          <input type="email" className="bk-input" value={form.email} onChange={set('email')} required autoComplete="off" />
        </label>
        <label className="bk-label">{isStaffForm ? 'Phone (optional)' : 'Phone'}
          <input type="tel" className="bk-input" value={form.phone} onChange={set('phone')} required={!isStaffForm} autoComplete="off" />
        </label>
        <fieldset className="ml-create-roles">
          <legend className="bk-label">Roles</legend>
          <small className="bk-muted">Leave them all unticked for a member, such as a parent or an adult who trains. Tick roles for staff.</small>
          {rolesError && <p className="bk-form-error" role="alert">{rolesError}</p>}
          {!clubRoles && !rolesError && <Skeleton rows={2} label="Loading roles…" />}
          {clubRoles?.map(r => (
            <label key={r.id} className="ml-create-roles__item">
              <input type="checkbox" className="bk-checkbox" checked={form.roleIds.includes(r.id)} onChange={() => toggleRole(r.id)} />
              <span>
                <strong>{r.name}</strong>
                <small className="bk-muted">{roleSummary(r, featureOn)}</small>
              </span>
            </label>
          ))}
        </fieldset>
        {error && <p className="bk-form-error" role="alert">{error}</p>}
      </div>
    </Sheet>
  );
}

export default function AdminMembers() {
  const bgOn = useFeature('bgNumbers');
  const [members, setMembers] = useState([]);
  const [childrenByUser, setChildrenByUser] = useState({}); // userId: "First Last, ..."
  const [gymnastsCountByUser, setGymnastsCountByUser] = useState({});
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(false);
  const [gymnasts, setGymnasts] = useState([]);
  // Search and role filter live in the URL so Back from a profile restores them
  const [params, setParams] = useSearchParams();
  const search = params.get('q') || '';
  const roleFilter = params.get('role') || '';
  const setParam = (key, value) => {
    const next = new URLSearchParams(params);
    if (value) next.set(key, value); else next.delete(key);
    setParams(next, { replace: true });
  };
  const setSearch = (v) => setParam('q', v);
  const setRoleFilter = (v) => setParam('role', v);
  const [page, setPage] = useState(1);
  const [showCreate, setShowCreate] = useState(false);
  const [createNote, setCreateNote] = useState(null);
  const toast = useToast();
  // Creating users is Club admin only on the server, as are the club's roles
  const { isClubAdmin } = useAuth();

  useEffect(() => { setPage(1); }, [search, roleFilter]);

  const load = useCallback(() =>
    bookingApi.getMembers({ includeArchived: true })
      .then(r => {
        const data = Array.isArray(r.data) ? r.data : r.data.users || [];
        const users = data.filter(u => !u.isGymnast);
        const kids = data.filter(u => u.isGymnast);

        // Build map: userId to joined child names for searching
        const map = {};
        kids.forEach(g => {
          (g.guardianIds || []).forEach(uid => {
            map[uid] = map[uid] ? `${map[uid]} ${g.firstName} ${g.lastName}` : `${g.firstName} ${g.lastName}`;
          });
        });

        // Build gymnast count per guardian
        const countMap = {};
        kids.forEach(g => {
          (g.guardianIds || []).forEach(uid => {
            countMap[uid] = (countMap[uid] || 0) + 1;
          });
        });
        setGymnastsCountByUser(countMap);

        setMembers(users);
        setGymnasts(kids);
        setChildrenByUser(map);
        setLoadError(false);
      })
      .catch(() => setLoadError(true))
      .finally(() => setLoading(false)), []);

  useEffect(() => { load(); }, [load]);

  const retry = () => { setLoading(true); setLoadError(false); load(); };

  const onCreated = (data) => {
    setShowCreate(false);
    if (data.emailSent) {
      setCreateNote(null);
      toast(`Account created. Password reset email sent to ${data.user.email}.`);
    } else {
      toast(`Account created for ${data.user.firstName} ${data.user.lastName}`);
      setCreateNote(`No email was sent to ${data.user.firstName} ${data.user.lastName}. Use "Send password reset" on their page to email them a login link.`);
    }
    load();
  };

  const q = search.trim().toLowerCase();
  const showArchived = roleFilter === 'ARCHIVED';
  const inView = members.filter(u => (showArchived ? u.isArchived : !u.isArchived));
  const filtered = inView.filter(u => {
    const matchesSearch = `${u.firstName} ${u.lastName} ${u.email} ${childrenByUser[u.id] || ''}`.toLowerCase().includes(q);
    const matchesRole = !roleFilter || showArchived || u.role === roleFilter;
    return matchesSearch && matchesRole;
  });

  // When searching, gymnasts whose name matches are listed first, by their own
  // name with their parent underneath; they open the parent's profile with that
  // child expanded. Adult participants link to their own account.
  const memberById = Object.fromEntries(inView.map(u => [u.id, u]));
  const childResults = q && (!roleFilter || roleFilter === 'ADULT' || showArchived)
    ? gymnasts
      .filter(g => `${g.firstName} ${g.lastName}`.toLowerCase().includes(q))
      .map(g => {
        const ownerId = g.userId && memberById[g.userId] ? g.userId : (g.guardianIds || []).find(id => memberById[id]);
        return ownerId ? { g, owner: memberById[ownerId], isSelf: ownerId === g.userId } : null;
      })
      .filter(Boolean)
    : [];
  // Members already shown only because of a matching child don't need their own row
  const childOwnerIds = new Set(childResults.map(r => r.owner.id));
  const memberResults = q
    ? filtered.filter(u => !childOwnerIds.has(u.id) || `${u.firstName} ${u.lastName} ${u.email}`.toLowerCase().includes(q))
    : filtered;

  const memberUrl = (userId, childId) => `/booking/admin/members/${userId}${childId ? `?child=${childId}` : ''}`;

  const totalPages = Math.max(1, Math.ceil(memberResults.length / PAGE_SIZE));
  const safePage = Math.min(page, totalPages);
  const paginated = memberResults.slice((safePage - 1) * PAGE_SIZE, safePage * PAGE_SIZE);

  const header = (
    <PageHeader
      back={{ to: '/admin-hub', label: 'Admin' }}
      title="Members"
      action={isClubAdmin && !loading && !loadError ? { label: 'Add member', short: 'Add', onClick: () => setShowCreate(true) } : undefined}
    />
  );

  if (loading) {
    return <div className="bk-page bk-page--xl">{header}<Skeleton rows={6} label="Loading members…" /></div>;
  }
  if (loadError) {
    return <div className="bk-page bk-page--xl">{header}<ErrorState title="Couldn't load members" onRetry={retry} /></div>;
  }

  return (
    <div className="bk-page bk-page--xl">
      {header}

      <ApprovalsWaiting />

      {createNote && <p className="ml-note" role="status">{createNote}</p>}

      <input
        className="bk-input ml-search"
        type="search"
        placeholder="Search gymnast, parent or email"
        aria-label="Search members"
        value={search}
        onChange={e => setSearch(e.target.value)}
      />

      <FilterChips label="Filter by role" chips={ROLE_CHIPS} value={roleFilter} onChange={setRoleFilter} />

      <div className="ml-list">
        {childResults.length === 0 && memberResults.length === 0 && <p className="bk-muted">No members found.</p>}
        {childResults.map(({ g, owner, isSelf }) => {
          const age = ageFrom(g.dateOfBirth);
          const bgFlag = bgOn && g.bgNumberStatus && g.bgNumberStatus !== 'VERIFIED';
          return (
            <Link key={`child-${g.id}`} className="ml-row" to={memberUrl(owner.id, g.id)}>
              <span className="ml-row__main">
                <span className="ml-row__name">{g.firstName} {g.lastName}</span>
                <span className="ml-row__sub">{isSelf ? 'Adult member' : `Parent: ${owner.firstName} ${owner.lastName}`}</span>
              </span>
              <span className="ml-row__pills">
                {bgFlag && <span className="bk-pill bk-pill--wait">BG {g.bgNumberStatus.toLowerCase().replace(/_/g, ' ')}</span>}
                {age != null && <span className="bk-pill bk-pill--off">Age {age}</span>}
              </span>
              <ChevronRightIcon className="ml-row__chev" aria-hidden="true" />
            </Link>
          );
        })}
        {paginated.map(u => {
          const count = gymnastsCountByUser[u.id] || 0;
          return (
            <Link key={u.id} className={`ml-row${u.isArchived ? ' ml-row--archived' : ''}`} to={memberUrl(u.id)}>
              <span className="ml-row__main">
                <span className="ml-row__name">{u.firstName} {u.lastName}</span>
                <span className="ml-row__sub">
                  {u.isArchived && u.archivedAt
                    ? `Archived ${formatDate(u.archivedAt)}${u.archivedReason ? ` · ${u.archivedReason}` : ''}`
                    : (childrenByUser[u.id] || (count === 1 ? '1 gymnast' : `${count} gymnasts`))}
                </span>
              </span>
              <span className="ml-row__pills">
                {bgOn && u.hasPendingBg && <span className="bk-pill bk-pill--wait">BG pending</span>}
                <span className="bk-pill bk-pill--info">{ROLE_LABELS[u.role] ?? u.role}</span>
              </span>
              <ChevronRightIcon className="ml-row__chev" aria-hidden="true" />
            </Link>
          );
        })}
      </div>

      {totalPages > 1 && (
        <div className="ml-pages">
          <button type="button" className="bk-btn bk-btn--sm" disabled={safePage <= 1} onClick={() => setPage(p => p - 1)}>Previous</button>
          <span className="bk-muted">Page {safePage} of {totalPages}</span>
          <button type="button" className="bk-btn bk-btn--sm" disabled={safePage >= totalPages} onClick={() => setPage(p => p + 1)}>Next</button>
        </div>
      )}

      <div className="ml-removed">
        <Disclosure summary="Removed members">
          <AdminRemovedMembers inline />
        </Disclosure>
      </div>

      {showCreate && isClubAdmin && <AddMemberSheet onCreated={onCreated} onClose={() => setShowCreate(false)} />}
    </div>
  );
}
